// 預估保存天數用平均位元率（VMS UX 評審第六輪 B6-07、R6-P1-5；SPEC #vms-web-290 第六輪）：
// 設備回報的 record_kbps 是當下的位元率（夜間 / 有人走動差很多），直接拿來估，總覽「最短預估保存」20 分鐘內在 16 → 17 → 27 天之間跳，
// 跟設備頁寫的 19 天也不一樣。改成：每台錄影主機在這台電腦記最近 24 小時的位元率樣本（每 5 分鐘最多一筆），用平均估；
// 總覽與設備頁都走這個函式，同一台同一個值。存不了（無痕模式）就只用這次讀到的樣本。
import type { StorageHealth } from './deviceApi';

const KEY = 'kinio.vms.recKbps.v1';
export const AVG_WINDOW_MS = 24 * 3600_000;
const MIN_GAP_MS = 5 * 60_000;
const MAX_HOSTS = 100;

type Samples = Record<string, [number, number][]>;

let mem: Samples | null = null;

function load(): Samples {
  if (mem) return mem;
  try {
    const v = JSON.parse(localStorage.getItem(KEY) ?? '{}') as Samples;
    mem = v && typeof v === 'object' ? v : {};
  } catch {
    mem = {};
  }
  return mem;
}

function save(s: Samples) {
  const keys = Object.keys(s);
  if (keys.length > MAX_HOSTS) {
    const newest = (k: string) => s[k][s[k].length - 1]?.[0] ?? 0;
    for (const k of keys.sort((a, b) => newest(a) - newest(b)).slice(0, keys.length - MAX_HOSTS)) delete s[k];
  }
  mem = s;
  try {
    localStorage.setItem(KEY, JSON.stringify(s));
  } catch {
    /* 存不了：這次有效就好 */
  }
}

/** 測試用 */
export function resetBitrateMemory() {
  mem = null;
}

/** 記一筆樣本並回最近 24 小時的平均位元率（kbps）。 */
export function averageKbps(host: string, kbps: number, now = Date.now()): number {
  const all = load();
  const list = (all[host] ?? []).filter(([t]) => now - t < AVG_WINDOW_MS && t <= now);
  const last = list[list.length - 1];
  if (!last || now - last[0] >= MIN_GAP_MS) {
    list.push([now, kbps]);
    save({ ...all, [host]: list });
  }
  const xs = list.map(([, v]) => v).filter((v) => v > 0);
  return xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : kbps;
}

/** 用平均位元率重算預估保存天數（overwrite = 保留 N 天時一樣封頂）；沒有位元率就原樣。 */
export function smoothRetention(host: string, s: StorageHealth | null, now = Date.now()): StorageHealth | null {
  if (!s || !s.recordKbps || s.recordKbps <= 0) return s;
  const avg = averageKbps(host, s.recordKbps, now);
  const perDay = ((avg * 1000) / 8) * 86400;
  const base = s.overwriteMode?.kind === 'off' ? s.freeBytes : s.totalBytes;
  if (base === undefined) return s;
  let days = base / perDay;
  if (s.overwriteMode?.kind === 'days') days = Math.min(s.overwriteMode.days, days);
  return { ...s, retentionDays: days, avgKbps: avg };
}
